import { useState, useCallback } from 'react'
import type { ChipValue } from '@/types'
import {
  performSpin,
  checkAllPaylines,
  calculateTotalWin,
  getWinningPositions,
  countScatters,
  getFreeSpins,
  type SlotSymbol,
  type WinResult,
  MAX_PAYLINE,
  MIN_PAYLINE,
} from '../utils/slotsLogic'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { delay } from '@/lib/utils'
import { recordGameTransaction } from '@/features/stats'

export type SlotsPhase = 'idle' | 'spinning' | 'showing-wins' | 'result'

interface UseSlotsGameReturn {
  // State
  visibleSymbols: SlotSymbol[][]
  activePaylines: number
  betPerLine: ChipValue
  totalBet: number
  phase: SlotsPhase
  wins: WinResult[]
  totalWin: number
  freeSpinsRemaining: number
  isSpinning: boolean
  winningPositions: [number, number][]
  lastWin: number

  // Actions
  setPaylines: (lines: number) => void
  setBetPerLine: (bet: ChipValue) => void
  spin: () => Promise<void>
  newGame: () => void
}

// Símbolos iniciales (display estático)
const INITIAL_SYMBOLS: SlotSymbol[][] = [
  ['diamond', 'gun', 'whiskey'],
  ['cards', 'wild', 'dice'],
  ['cigar', 'diamond', 'scatter'],
  ['whiskey', 'cards', 'gun'],
  ['dice', 'cigar', 'diamond'],
]

export function useSlotsGame(): UseSlotsGameReturn {
  const { user, updateBalance, isDemo } = useAuth()

  const [visibleSymbols, setVisibleSymbols] = useState<SlotSymbol[][]>(INITIAL_SYMBOLS)
  const [activePaylines, setActivePaylines] = useState(10)
  const [betPerLine, setBetPerLineState] = useState<ChipValue>(100)
  const [phase, setPhase] = useState<SlotsPhase>('idle')
  const [wins, setWins] = useState<WinResult[]>([])
  const [totalWin, setTotalWin] = useState(0)
  const [freeSpinsRemaining, setFreeSpinsRemaining] = useState(0)
  const [lastWin, setLastWin] = useState(0)

  // Computed
  const totalBet = betPerLine * activePaylines
  const isSpinning = phase === 'spinning'
  const winningPositions = getWinningPositions(wins)

  // Set paylines
  const setPaylines = useCallback((lines: number) => {
    if (phase !== 'idle' && phase !== 'result') return
    const clamped = Math.max(MIN_PAYLINE, Math.min(MAX_PAYLINE, lines))
    setActivePaylines(clamped)
  }, [phase])

  // Set bet per line
  const setBetPerLine = useCallback((bet: ChipValue) => {
    if (phase !== 'idle' && phase !== 'result') return
    setBetPerLineState(bet)
  }, [phase])

  // Main spin function
  const spin = useCallback(async () => {
    if (isSpinning) return
    if (!user) return

    const isFreeSpinMode = freeSpinsRemaining > 0
    const currentBet = isFreeSpinMode ? 0 : totalBet

    // Get current balance based on user type (demo vs real)
    const currentBalance = isDemo ? (user.demoBalance || 0) : (user.balance || 0)

    // Check balance (unless free spin)
    if (!isFreeSpinMode && currentBet > currentBalance) return

    // Deduct bet (unless free spin)
    if (!isFreeSpinMode) {
      const success = await updateBalance(-currentBet)
      if (!success) return
    }

    // Start spinning
    setPhase('spinning')
    setWins([])
    setTotalWin(0)
    setLastWin(0)

    // Animation delay (2.5 seconds for all reels to stop)
    await delay(2500)

    // Generate result
    const newSymbols = performSpin()
    setVisibleSymbols(newSymbols)

    // Check for wins
    const newWins = checkAllPaylines(newSymbols, activePaylines, betPerLine)
    const winAmount = calculateTotalWin(newWins)

    // Check for scatters (free spins)
    const scatterCount = countScatters(newSymbols)
    const newFreeSpins = getFreeSpins(scatterCount)

    // Update state
    setWins(newWins)
    setTotalWin(winAmount)
    setLastWin(winAmount)

    // Handle free spins
    if (isFreeSpinMode) {
      setFreeSpinsRemaining((prev) => prev - 1 + newFreeSpins)
    } else if (newFreeSpins > 0) {
      setFreeSpinsRemaining(newFreeSpins)
    }

    // Show wins phase
    if (newWins.length > 0 || newFreeSpins > 0) {
      setPhase('showing-wins')
      await delay(2000) // Show winning animation
    }

    // Pay winnings
    if (winAmount > 0) {
      await updateBalance(winAmount)
    }

    // Record transaction (only for real users, not demo)
    if (!isDemo && user.uid) {
      const betUsed = isFreeSpinMode ? 0 : currentBet
      const result = winAmount > betUsed ? 'win' : winAmount === betUsed ? 'push' : 'loss'

      recordGameTransaction(
        user.uid,
        user.email,
        user.displayName || user.name || 'Usuario',
        'slots',
        betUsed,
        winAmount,
        result,
        {
          paylines: activePaylines,
          betPerLine,
          symbols: newSymbols.map((reel) => reel.join(',')).join('|'),
          wins: newWins.map((w) => ({
            line: w.paylineIndex + 1,
            symbol: w.symbol,
            count: w.count,
            payout: w.payout,
          })),
          freeSpins: newFreeSpins,
          isFreeSpinMode,
        }
      )
    }

    setPhase('result')
  }, [
    isSpinning,
    user,
    freeSpinsRemaining,
    totalBet,
    updateBalance,
    activePaylines,
    betPerLine,
    isDemo,
  ])

  // New game (reset state)
  const newGame = useCallback(() => {
    setVisibleSymbols(INITIAL_SYMBOLS)
    setPhase('idle')
    setWins([])
    setTotalWin(0)
    setLastWin(0)
    // Don't reset free spins - they persist
  }, [])

  return {
    // State
    visibleSymbols,
    activePaylines,
    betPerLine,
    totalBet,
    phase,
    wins,
    totalWin,
    freeSpinsRemaining,
    isSpinning,
    winningPositions,
    lastWin,

    // Actions
    setPaylines,
    setBetPerLine,
    spin,
    newGame,
  }
}
